import { useEffect, useSyncExternalStore } from 'react';
import crossIcon from '@/assets/icons/cross.svg';
import type { FooterCampaignData } from '@/types/app';
import './FooterCampaign.scss';

interface FooterCampaignProps {
  campaign?: FooterCampaignData | null;
}

const STORAGE_PREFIX = 'footer_campaign_dismissed_';

// sessionStorage.setItem never fires 'storage' in the tab that wrote it, so
// dismissal needs a manual notify() to move useSyncExternalStore subscribers.
const dismissalListeners = new Set<() => void>();

function subscribeDismissal(callback: () => void) {
  dismissalListeners.add(callback);
  return () => dismissalListeners.delete(callback);
}

function notifyDismissalChanged() {
  dismissalListeners.forEach((listener) => listener());
}

// Fallback when sessionStorage throws (private mode, quota) — the close
// button must still hide the banner for the current session.
const sessionDismissed = new Set<number>();

function isCampaignDismissed(campaignId: number): boolean {
  if (sessionDismissed.has(campaignId)) {
    return true;
  }
  try {
    return window.sessionStorage.getItem(STORAGE_PREFIX + campaignId) === '1';
  } catch {
    return false;
  }
}

function getServerSnapshot(): boolean {
  return false;
}

export function FooterCampaign({ campaign }: FooterCampaignProps) {
  const dismissed = useSyncExternalStore(
    subscribeDismissal,
    () => (campaign ? isCampaignDismissed(campaign.id) : false),
    getServerSnapshot,
  );

  const isVisible
    = !!campaign
    && !dismissed
    && campaign.image !== ''
    && campaign.devices.length > 0;

  useEffect(() => {
    if (!isVisible || typeof document === 'undefined') {
      return;
    }
    document.documentElement.style.setProperty(
      '--footer-campaign-height',
      '110px',
    );
    return () => {
      document.documentElement.style.removeProperty('--footer-campaign-height');
    };
  }, [isVisible]);

  if (!isVisible) {
    return null;
  }

  const handleClose = () => {
    sessionDismissed.add(campaign.id);
    try {
      window.sessionStorage.setItem(STORAGE_PREFIX + campaign.id, '1');
    } catch {
      // sessionStorage unavailable: the in-memory Set takes over
    }
    notifyDismissalChanged();
  };

  const visibleClass = campaign.devices
    .map((device) => `footer-campaign--show-${device}`)
    .join(' ');

  return (
    <div className={`footer-campaign ${visibleClass}`} role="complementary">
      <a
        className="footer-campaign__link"
        href={campaign.url}
        target="_blank"
        rel="noopener noreferrer sponsored"
        aria-label="Bannière publicitaire"
      >
        <img
          src={campaign.image}
          alt=""
          className="footer-campaign__image"
          loading="lazy"
          decoding="async"
          width={970}
          height={110}
        />
      </a>
      <button
        type="button"
        className="footer-campaign__close"
        onClick={handleClose}
        aria-label="Fermer la bannière"
      >
        <img src={crossIcon} alt="" aria-hidden="true" width="14" height="14" />
      </button>
    </div>
  );
}

export default FooterCampaign;
